import { Web3DViewerWrapper } from '@trimble/web3d-utils';
import { CapabilityBase } from '@trimble/web3d-capabilities';
import { CapabilityProviderBase } from '@trimble/web3d-providers';
import { Accessor, createSignal, Setter } from 'solid-js';
import { CapabilityRegistryContainer } from './capability-registry-container';
import { CapabilityRegistryBase } from './capability-registry-base';
import {
  CapabilityBaseT,
  Constructor,
  ParentResourceCollections,
  ResourceCollections,
} from './capability-registry-types';

/**
 * Central repository for storing and accessing global and per pane capabilities and providers.
 */
export class CapabilityRegistry
  extends CapabilityRegistryBase
  implements ResourceCollections
{
  public readonly paneContainers: Accessor<CapabilityRegistryContainer[]>;
  private readonly setPaneContainers: Setter<CapabilityRegistryContainer[]>;

  public readonly sharedProviders: ParentResourceCollections['sharedProviders'];
  public readonly capabilities: ResourceCollections['capabilities'];
  public readonly providers: ResourceCollections['providers'];
  public readonly coordinators: ResourceCollections['coordinators'];

  constructor() {
    super();

    const [paneContainers, setPaneContainers] = createSignal<
      CapabilityRegistryContainer[]
    >([]);
    this.paneContainers = paneContainers;
    this.setPaneContainers = setPaneContainers;

    this.capabilities = this.collectCapabilities();
    this.providers = this.collectProviders();
    this.sharedProviders = this.collectSharedProviders();
    this.coordinators = this.collectCoordinators();
  }

  public registerPaneContainer(
    viewerWrapper?: Web3DViewerWrapper,
    paneRowIdx?: number,
    paneCellIdx?: number
  ): CapabilityRegistryContainer {
    const container = new CapabilityRegistryContainer(this, viewerWrapper);
    container.paneRowIdx = paneRowIdx;
    container.paneCellIdx = paneCellIdx;
    this.setPaneContainers([...this.paneContainers(), container]);

    if (viewerWrapper) {
      this.addViewerToSharedProviders(viewerWrapper);
    }

    return container;
  }

  public removePaneContainer(container: CapabilityRegistryContainer): void {
    this.setPaneContainers(
      this.paneContainers().filter((c) => c !== container)
    );
  }

  public unregisterPaneContainer(viewerWrapper: Web3DViewerWrapper): void {
    const containers = this.paneContainers();
    const index = containers.findIndex(
      (container) => container.viewerWrapper() === viewerWrapper
    );

    if (index !== -1) {
      this.setPaneContainers(containers.filter((_, i) => i !== index));
    }

    this.removeViewerFromSharedProviders(viewerWrapper);
    viewerWrapper.viewer.dispose();
  }

  public getProvider<T extends CapabilityProviderBase<CapabilityBase>>(
    providerClass: Constructor<T>
  ): Accessor<T | undefined> {
    return this.get(providerClass);
  }

  public getPaneCapabilities<T extends CapabilityBaseT>(
    providerClass: Constructor<T>
  ): T[] {
    return this.paneContainers()
      .map((pc) => pc.get(providerClass)())
      .filter((p): p is T => p !== undefined);
  }

  public getCapability<T extends CapabilityBase>(
    capabilityClass: Constructor<T>
  ): Accessor<T | undefined> {
    return this.get(capabilityClass);
  }

  public async cleanup(): Promise<void> {
    await Promise.all(
      [
        ...this.capabilities(),
        ...this.providers(),
        ...this.sharedProviders(),
        ...this.coordinators(),
      ].map((c) => c.cleanup())
    );

    this.clearRegistry();
    this.setPaneContainers([]);
  }

  private addViewerToSharedProviders(viewerWrapper: Web3DViewerWrapper): void {
    const providers = this.sharedProviders();
    providers.forEach((provider) => {
      provider.addViewer(viewerWrapper);
    });
  }

  private removeViewerFromSharedProviders(
    viewerWithPlugins: Web3DViewerWrapper
  ): void {
    const providers = this.sharedProviders();
    providers.forEach((provider) => {
      provider.removeViewer(viewerWithPlugins);
    });
  }
}
